import { useState } from 'react'
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  CheckCircle,
  User,
  Building,
  MessageSquare
} from 'lucide-react'

const contactInfo = [
  {
    icon: Phone,
    title: '联系电话',
    content: '177-7800-2535',
    href: 'tel:17778002535',
    description: '工作日 9:00-18:00',
  },
  {
    icon: Mail,
    title: '电子邮箱',
    content: 'market@oneec.cn',
    href: 'mailto:market@oneec.cn',
    description: '商务合作咨询',
  },
  {
    icon: MapPin,
    title: '公司地址',
    content: '北京市石景山区古城大街中海大厦',
    description: '欢迎来访洽谈',
  },
  {
    icon: Clock,
    title: '服务时间',
    content: '7×24小时',
    description: '智能客服全天候',
  },
]

const serviceValues = [
  {
    icon: CheckCircle,
    title: '智能速应',
    description: 'AI智能客服系统，7×24小时不间断响应',
  },
  {
    icon: CheckCircle,
    title: '全域服务',
    description: '从合作洽谈至长期运维，搭建"需求-交付-复盘"闭环',
  },
  {
    icon: CheckCircle,
    title: '专属直达',
    description: '1V1专属服务小组，含行业顾问、技术专家',
  },
  {
    icon: CheckCircle,
    title: '应急稳续',
    description: '24小时待命应急小组，快速处理故障类问题',
  },
]

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    await new Promise((resolve) => setTimeout(resolve, 1500))
    setIsSubmitting(false)
    setIsSubmitted(true)
    setFormData({ name: '', company: '', phone: '', email: '', message: '' })
  }

  return (
    <div className="overflow-hidden bg-dark">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0">
          <div className="absolute inset-0 radial-glow opacity-50" />
          <div className="absolute inset-0 grid-pattern opacity-30" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center space-x-2 px-4 py-2 rounded-full glass-card mb-6">
              <span className="w-2 h-2 rounded-full bg-neon-green animate-pulse" />
              <span className="text-neon-green text-sm font-medium">联系我们</span>
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              开启您的<span className="text-gradient">数字化之旅</span>
            </h1>
            <p className="text-lg text-white/50 leading-relaxed">
              无论您有任何问题或需求，我们的专业团队都将竭诚为您服务
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {contactInfo.map((item, index) => (
              <div
                key={item.title}
                className="glass-card p-6 hover:border-neon-green/30 transition-all group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-12 h-12 rounded-xl bg-neon-green/10 flex items-center justify-center mb-4 group-hover:bg-neon-green group-hover:scale-110 transition-all">
                  <item.icon className="w-6 h-6 text-neon-green group-hover:text-dark transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                {item.href ? (
                  <a href={item.href} className="text-neon-green font-medium hover:underline">
                    {item.content}
                  </a>
                ) : (
                  <p className="text-white font-medium">{item.content}</p>
                )}
                <p className="text-white/50 text-sm mt-1">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Values */}
      <section className="py-12 md:py-16 relative">
        <div className="absolute inset-0 radial-glow opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              我们的服务<span className="text-gradient">承诺</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {serviceValues.map((value, index) => (
              <div
                key={value.title}
                className="glass-card p-6 text-center hover:border-neon-green/30 transition-all group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-12 h-12 rounded-xl bg-neon-green/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-neon-green group-hover:scale-110 transition-all">
                  <value.icon className="w-6 h-6 text-neon-green group-hover:text-dark transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{value.title}</h3>
                <p className="text-white/50 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-card p-8 md:p-10">
            <div className="text-center mb-8">
              <div className="w-14 h-14 rounded-xl bg-neon-green/10 flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-7 h-7 text-neon-green" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">在线留言</h2>
              <p className="text-white/50">填写以下表单，我们将尽快与您联系</p>
            </div>

            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-full bg-neon-green/20 flex items-center justify-center mx-auto mb-6 neon-glow">
                  <CheckCircle className="w-10 h-10 text-neon-green" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">提交成功！</h3>
                <p className="text-white/50 mb-6">感谢您的留言，我们的团队将在24小时内与您联系</p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="px-6 py-3 bg-neon-green text-dark rounded-xl font-semibold hover:bg-neon-green/90 transition-all"
                >
                  继续留言
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white font-medium mb-2 flex items-center">
                      <User className="w-4 h-4 mr-2 text-neon-green" />
                      您的姓名 *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-neon-green transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label className="block text-white font-medium mb-2 flex items-center">
                      <Building className="w-4 h-4 mr-2 text-neon-green" />
                      公司名称
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-neon-green transition-colors"
                      placeholder="请输入公司名称"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white font-medium mb-2 flex items-center">
                      <Phone className="w-4 h-4 mr-2 text-neon-green" />
                      联系电话 *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-neon-green transition-colors"
                      placeholder="请输入联系电话"
                    />
                  </div>
                  <div>
                    <label className="block text-white font-medium mb-2 flex items-center">
                      <Mail className="w-4 h-4 mr-2 text-neon-green" />
                      电子邮箱
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-neon-green transition-colors"
                      placeholder="请输入电子邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2 flex items-center">
                    <MessageSquare className="w-4 h-4 mr-2 text-neon-green" />
                    留言内容 *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-neon-green transition-colors resize-none"
                    placeholder="请详细描述您的需求或问题..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-neon-green text-dark rounded-xl font-semibold hover:bg-neon-green/90 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 neon-glow"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-dark/30 border-t-dark rounded-full animate-spin" />
                      <span>提交中...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>提交留言</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="py-12 md:py-16 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-card p-2">
            <div className="aspect-video rounded-xl bg-gradient-to-br from-dark-lighter to-dark flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 grid-pattern opacity-30" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="w-16 h-16 rounded-full bg-neon-green/20 flex items-center justify-center neon-glow">
                  <MapPin className="w-8 h-8 text-neon-green" />
                </div>
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="glass-card p-4 inline-block">
                  <p className="text-white font-medium">北京市石景山区古城大街中海大厦</p>
                  <p className="text-white/50 text-sm">北京壹电利科技有限公司</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
